import { useMemo } from 'react';
import type { GiftCard, GiftProgressState } from '../../../offer.types';

interface UseGiftProgressArgs {
  gifts: GiftCard[] | undefined | null;
  baseSubtotal: number;
  baseItemCount: number;
  appliedCouponCode: string | null | undefined;
}

/**
 * Computes per-gift unlock state for the checkout UI.
 *
 * The backend is the source of truth for whether a gift becomes a real line
 * item with price=0 (see `unified-line-items.service.buildGiftItems`). This
 * hook mirrors that logic locally so the customer sees the correct progress
 * bar and CTA without waiting for a roundtrip on every interaction.
 *
 * Base inputs (`baseSubtotal`, `baseItemCount`) MUST match what the backend
 * uses: offer items + accepted order bumps, before coupon and shipping, and
 * never include the gift itself.
 */
export function useGiftProgress({
  gifts,
  baseSubtotal,
  baseItemCount,
  appliedCouponCode,
}: UseGiftProgressArgs): GiftProgressState[] {
  return useMemo(() => {
    if (!gifts || gifts.length === 0) return [];

    const normalizedApplied = appliedCouponCode
      ? appliedCouponCode.trim().toUpperCase()
      : null;

    return gifts.map((gift): GiftProgressState => {
      const required = gift.couponCodeRequired
        ? gift.couponCodeRequired.trim().toUpperCase()
        : null;

      let progress = 0;
      let thresholdReached = false;
      let remainingValue: number | null = null;
      let remainingQuantity: number | null = null;

      if (gift.triggerType === 'always') {
        progress = 1;
        thresholdReached = true;
      } else if (gift.triggerType === 'value') {
        const min = gift.triggerMinValue ?? 0;
        if (min > 0) {
          progress = Math.min(1, Math.max(0, baseSubtotal / min));
          thresholdReached = baseSubtotal >= min;
          remainingValue = thresholdReached ? 0 : Number((min - baseSubtotal).toFixed(2));
        }
      } else if (gift.triggerType === 'quantity') {
        const min = gift.triggerMinQuantity ?? 0;
        if (min > 0) {
          progress = Math.min(1, Math.max(0, baseItemCount / min));
          thresholdReached = baseItemCount >= min;
          remainingQuantity = thresholdReached ? 0 : Math.max(0, min - baseItemCount);
        }
      }

      let status: GiftProgressState['status'] = 'locked';
      if (thresholdReached) {
        if (!required) {
          status = 'unlocked';
        } else if (required === normalizedApplied) {
          status = 'unlocked';
        } else {
          status = 'unlocked_needs_coupon';
        }
      }

      return {
        gift,
        status,
        thresholdReached,
        progress,
        remainingValue,
        remainingQuantity,
      };
    });
  }, [gifts, baseSubtotal, baseItemCount, appliedCouponCode]);
}
